/**
 * PropsUpsellCard — the teaser's conversion card for non-subscribers
 *
 * Sits ABOVE the board, mirroring OddsShopper's arbitrage taster (the best
 * converting surface there): a live counter bar quantified from the server's
 * `lockedTeaser` summary (never fabricated), the top few locked plays with the
 * EV pill visible and the actionable side/line/book withheld, and a footer that
 * counts the rest next to the tagged checkout CTA. The free row(s) render in
 * the grid underneath. Signed-out visitors also get a Sign in secondary.
 *
 * Only mounted in teaser mode (see PropsDesktop `isTeaser`), which already
 * excludes the native app — Apple Guideline 3.1.1 forbids in-app purchase
 * deep-links and subscribe messaging, so the app keeps its neutral "no access"
 * screen instead. Access state comes from PropsAccessContext.
 */

import React from 'react';
import { Box, Button, Link, Paper, Stack, Typography } from 'omaha-design-system';
import { LockKey as LockKeyIcon, WarningCircle as WarningCircleIcon } from '@phosphor-icons/react';
import { LockedTeaser, LockedTeaserRow } from '../data/types';
import { normalizeStatLabel } from '../data/statLabels';
import { formatEventTime } from '../data/transformPropsData';
import { usePropsAccess } from '../hooks/usePropsAccess';
import {
  buildPropsPurchaseUrl,
  PROPS_PROMO_CODE,
  PROPS_PROMO_LABEL,
  SUPPORT_EMAIL,
  UPSELL_EV_THRESHOLD_PCT,
  trackPropsEvent,
} from '../helpers/propsConversion';

const GREEN = '#66BB6A';
const FONT = '"Instrument Sans", sans-serif';
const DIVIDER = '1px solid rgba(255, 255, 255, 0.08)';

export interface PropsUpsellCardProps {
  league: string;
  /** Server summary of the locked rows (present whenever the proxy served the teaser shape). */
  lockedTeaser: LockedTeaser;
  /** Unlocked rows currently on the board (after the viewer's filters) — normally 1. */
  freeCount: number;
  isMobile: boolean;
  onSignIn: () => void;
}

const EvPill: React.FC<{ evPct: number }> = ({ evPct }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.5,
      height: 32,
      borderRadius: '100px',
      border: `1px solid ${GREEN}`,
      backgroundColor: 'rgba(102, 187, 106, 0.12)',
      flexShrink: 0,
    }}
  >
    <LockKeyIcon size={14} weight="fill" color={GREEN} />
    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14, color: GREEN, whiteSpace: 'nowrap' }}>
      +{evPct.toFixed(1)}% EV
    </Typography>
  </Box>
);

const TeaserRow: React.FC<{ row: LockedTeaserRow; league: string }> = ({ row, league }) => {
  // Same formatter as the game chips: bare time, weekday-prefixed for the NFL's multi-day slate
  const kickoff = formatEventTime(row.eventStartTime, league);
  const who = [row.position, row.team].filter(Boolean).join(' - ');
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: 2, py: 1.5, borderBottom: DIVIDER }}>
      <Stack sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 15, color: '#F2F2F3' }}>
          {row.playerName}
          {who && (
            <Box component="span" sx={{ fontWeight: 400, fontSize: 13, color: 'rgba(242, 242, 243, 0.6)', ml: 1 }}>
              {who}
            </Box>
          )}
        </Typography>
        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, color: 'rgba(242, 242, 243, 0.6)' }}>
          {[normalizeStatLabel(row.stat), kickoff !== 'TBD' ? kickoff : '', 'Side & book locked'].filter(Boolean).join(' · ')}
        </Typography>
      </Stack>
      <EvPill evPct={row.evPct} />
    </Stack>
  );
};

export const PropsUpsellCard: React.FC<PropsUpsellCardProps> = ({ league, lockedTeaser, freeCount, isMobile, onSignIn }) => {
  const { isAuthenticated, checkFailed, recheckSubscription } = usePropsAccess();
  const positiveEvCount = lockedTeaser.count;
  const maxEvPct = lockedTeaser.maxEvPct;
  const rows = lockedTeaser.rows;
  const remaining = Math.max(positiveEvCount - rows.length, 0);
  const purchaseUrl = buildPropsPurchaseUrl(league);

  const handleCtaClick = () => {
    trackPropsEvent('props_teaser_cta_click', {
      league,
      positive_ev_count: positiveEvCount,
      max_locked_ev_pct: maxEvPct,
      is_mobile: isMobile ? 1 : 0,
      is_authenticated: isAuthenticated ? 1 : 0,
    });
  };

  const cardSx = {
    // The board column is a fixed-height flex stack with the grid at flex: 1 — never let it shrink the card
    flexShrink: 0,
    mb: 2,
    mx: isMobile ? 2 : 0,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#161617',
    border: `1px solid ${GREEN}`,
    boxShadow: '0 0 20px rgba(102, 187, 106, 0.2)',
  };

  const secondaryButtonSx = {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: 14,
    textTransform: 'none' as const,
    borderRadius: '8px',
    borderColor: GREEN,
    color: GREEN,
    whiteSpace: 'nowrap' as const,
    '&:hover': { borderColor: GREEN, backgroundColor: 'rgba(102, 187, 106, 0.08)' },
  };

  const supportLink = (
    <Typography sx={{ fontFamily: FONT, fontSize: 11, color: 'rgba(242, 242, 243, 0.45)' }}>
      Already subscribed?{' '}
      <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: '#4DA3FF', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
        Contact Support
      </Link>
    </Typography>
  );

  // The check errored, or the server locked the board for someone the client thinks is
  // subscribed (e.g. a silent token failure): offer a retry, don't sell to a paying user.
  if (checkFailed) {
    return (
      <Paper sx={{ ...cardSx, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningCircleIcon size={20} color="#EF5350" />
            <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#F2F2F3' }}>
              We couldn&apos;t verify your subscription — showing the limited preview until we can.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {supportLink}
            <Button variant="outlined" onClick={recheckSubscription} sx={{ ...secondaryButtonSx, borderColor: 'rgba(255,255,255,0.3)', color: '#F2F2F3' }}>
              Retry
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={cardSx}>
      {/* Live counter bar — quantified from the server summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 2, py: 1.25, borderBottom: DIVIDER, backgroundColor: 'rgba(102, 187, 106, 0.08)' }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <LockKeyIcon size={18} weight="fill" color={GREEN} style={{ flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#F2F2F3' }}>
            {positiveEvCount > 0 ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {positiveEvCount} +EV {league} prop{positiveEvCount === 1 ? '' : 's'} above +{UPSELL_EV_THRESHOLD_PCT}% EV
                </Box>{' '}
                {positiveEvCount === 1 ? 'is' : 'are'} live right now — hidden on your plan
              </>
            ) : (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  No {league} props above +{UPSELL_EV_THRESHOLD_PCT}% EV right now
                </Box>{' '}
                — check back as lines move
              </>
            )}
          </Typography>
        </Stack>
        {maxEvPct > 0 && (
          <Typography
            sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14, color: GREEN, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
          >
            Up to +{maxEvPct.toFixed(1)}% EV
          </Typography>
        )}
      </Stack>

      {/* Top locked plays — who/what/when + EV; side, line, book and odds never reach the client */}
      {rows.map((row, i) => (
        <TeaserRow key={`${row.playerName}|${row.stat}|${i}`} row={row} league={league} />
      ))}

      {/* Footer — count the rest + CTAs */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: 'rgba(242, 242, 243, 0.6)', minWidth: 0 }}>
          {remaining > 0
            ? `+ ${remaining} more above +${UPSELL_EV_THRESHOLD_PCT}% EV — projections, best line, EV and every book on each one`
            : positiveEvCount > 0
              ? 'Projections, best line, EV and every book on each one'
              : 'Full projections, best lines and EV on every prop'}
          {freeCount > 0 && ` · your top pick stays free below`}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          {!isAuthenticated && (
            <Button variant="outlined" onClick={onSignIn} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
              Already a subscriber? Sign in
            </Button>
          )}
          {/* Real link: the anchor navigates, the handler only records the click */}
          <Button
            variant="contained"
            component="a"
            href={purchaseUrl}
            onClick={handleCtaClick}
            startIcon={<LockKeyIcon size={18} weight="fill" />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              px: 3,
              py: 1.25,
              bgcolor: '#005CCE',
              color: 'white',
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 15,
              textTransform: 'none',
              borderRadius: '8px',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              '&:hover': { bgcolor: '#0048A8' },
            }}
          >
            Unlock {league} Props — {PROPS_PROMO_LABEL}
          </Button>
        </Stack>
      </Stack>

      {/* Fine print */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.25, sm: 2 }}
        justifyContent="flex-end"
        sx={{ px: 2, pb: 1.25, mt: -0.5 }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: 'rgba(242, 242, 243, 0.45)' }}>
          Code {PROPS_PROMO_CODE} applied at checkout
        </Typography>
        {isAuthenticated && (
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: 'rgba(242, 242, 243, 0.45)' }}>
            Just subscribed?{' '}
            <Link component="button" type="button" onClick={recheckSubscription} sx={{ fontFamily: FONT, fontSize: 11, color: '#4DA3FF', verticalAlign: 'baseline' }}>
              Refresh access
            </Link>
            {' '}(up to 10 min)
          </Typography>
        )}
        {supportLink}
      </Stack>
    </Paper>
  );
};

export default PropsUpsellCard;
